// Safari compatibility helper functions

// Detect Safari browser
export const isSafari = () => {
  const ua = navigator.userAgent;
  const chrome = ua.indexOf('Chrome') > -1;
  const safari = ua.indexOf('Safari') > -1;
  return safari && !chrome;
};

// Detect iOS Safari
export const isIOSSafari = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Detect Safari version
export const getSafariVersion = () => {
  const match = navigator.userAgent.match(/Version\/(\d+\.\d+)/);
  return match ? parseFloat(match[1]) : null;
};

// Check if feature is supported
export const isFeatureSupported = (feature) => {
  const safariVersion = getSafariVersion();
  const features = {
    'backdrop-filter': safariVersion >= 9,
    'gap': safariVersion >= 14.1,
    'aspect-ratio': safariVersion >= 15,
    'webshare': isIOSSafari(), // Only on iOS Safari
    'fullscreen': safariVersion >= 5.1,
  };
  return features[feature] || false;
};

// Fullscreen API wrapper for Safari compatibility
export const fullscreenAPI = {
  request: (element) => {
    if (element.requestFullscreen) {
      return element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      return element.webkitRequestFullscreen();
    } else if (element.webkitEnterFullscreen) {
      return element.webkitEnterFullscreen();
    }
    return Promise.reject(new Error('Fullscreen not supported'));
  },
  
  exit: () => {
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    } else if (document.webkitCancelFullScreen) {
      return document.webkitCancelFullScreen();
    }
    return Promise.reject(new Error('Exit fullscreen not supported'));
  },
  
  isFullscreen: () => {
    return !!(document.fullscreenElement || 
              document.webkitFullscreenElement || 
              document.webkitCurrentFullScreenElement);
  },
  
  getFullscreenElement: () => {
    return document.fullscreenElement || 
           document.webkitFullscreenElement || 
           document.webkitCurrentFullScreenElement;
  }
};

// Clipboard API wrapper for Safari compatibility
export const clipboardAPI = {
  writeText: async (text) => {
    // Modern clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Modern clipboard API failed:', err);
      }
    }
    
    // Fallback for Safari and older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      // Special handling for iOS Safari
      if (isIOSSafari()) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, 999999);
      }
      
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    } catch (err) {
      console.error('Fallback clipboard method failed:', err);
      document.body.removeChild(textArea);
      return false;
    }
  }
};

// Video element configuration for Safari
export const configureVideoForSafari = (videoElement) => {
  if (!videoElement) return;
  
  // Set Safari-specific attributes
  videoElement.setAttribute('playsinline', '');
  videoElement.setAttribute('webkit-playsinline', 'true');
  videoElement.setAttribute('x-webkit-airplay', 'allow');
  
  // For iOS Safari autoplay
  if (isIOSSafari()) {
    videoElement.muted = true; // Required for autoplay on iOS
  }
};

// Get media constraints for Safari
export const getMediaConstraints = (constraints = {}) => {
  const defaultConstraints = {
    audio: true,
    video: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode: 'user'
    }
  };
  
  // Safari-specific adjustments
  if (isSafari()) {
    // Safari may have issues with certain constraints
    if (constraints.video && typeof constraints.video === 'object') {
      // Remove advanced constraints that Safari might not support
      delete constraints.video.frameRate;
      delete constraints.video.aspectRatio;
    }
  }
  
  return { ...defaultConstraints, ...constraints };
};

// Apply Safari-specific CSS fixes
export const applySafariCSSFixes = () => {
  if (!isSafari()) return;
  
  const style = document.createElement('style');
  style.textContent = `
    /* Safari-specific CSS fixes */
    @supports (-webkit-backdrop-filter: none) {
      .safari-backdrop-fix {
        -webkit-backdrop-filter: blur(10px);
        backdrop-filter: blur(10px);
      }
    }
    
    /* Fix for Safari flexbox gap */
    @supports not (gap: 1rem) {
      .safari-flex-gap > * + * {
        margin-left: 1rem;
      }
    }
    
    /* Fix for Safari sticky positioning */
    .safari-sticky {
      position: -webkit-sticky;
      position: sticky;
    }
    
    /* Fix for Safari transform */
    .safari-transform {
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
    }
  `;
  
  document.head.appendChild(style);
};

// Initialize Safari fixes on load
if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', applySafariCSSFixes);
}